import { Box, Flex, Text } from "rebass/styled-components";
import styled from "styled-components";
import Countdown from "react-countdown";
import { IoCheckmark, IoLockClosed } from "react-icons/io5";
import { RiTimeLine } from "react-icons/ri";
import { useRecoilState } from "recoil";
import { useHistory } from "react-router-dom";

import theme from "theme/themeThree";
import { CountDownRenderer } from "MVVM/Views/Competitions/Views/Play/countdownTimer";
import { analytics } from "MVVM/Analytics";
import { StripePaystationState } from "MVVM/State";
import { ClipQuota } from "MVVM/Utilities/ClipLimits";
import {
  TIER_DATA,
  TIER_HIGHLIGHTS,
} from "MVVM/Views/Pricing/Components/PricingCards/features";
import { TTierKey } from "MVVM/Views/Pricing/Components/PricingCards/types";
import {
  IHighlightItem,
  IHighlightSection,
} from "MVVM/Views/Pricing/Components/PricingCards/types";
import { ReactComponent as EssentialsIconSVG } from "../../../Assets/Avatar/Icons/essentials.svg";
import { ReactComponent as ProIconSVG } from "../../../Assets/Avatar/Icons/pro.svg";

interface IClipEligibilityModalProps {
  clipQuota?: ClipQuota;
  userGroup?: string;
  onClose: () => void;
  closeQuickEditModal?: () => void;
}

const TIER_ACCENT: Record<string, string> = {
  ESSENTIALS: theme.colors.envy,
  PRO: theme.colors.champion,
};

const getHeaderGradient = (tierKey: string) => {
  switch (tierKey) {
    case "ESSENTIALS":
      return `linear-gradient(135deg, ${theme.colors.envyAlpha20} 0%, transparent 60%)`;
    case "PRO":
      return `linear-gradient(135deg, ${theme.colors.championAlpha70} 0%, transparent 60%)`;
    default:
      return `linear-gradient(135deg, ${theme.colors.envyAlpha20} 0%, transparent 60%)`;
  }
};

const getButtonGradient = (tierKey: string) => {
  switch (tierKey) {
    case "ESSENTIALS":
      return `linear-gradient(135deg, ${theme.colors.envy} 0%, ${theme.colors.darkerEnvy} 100%)`;
    case "PRO":
      return `linear-gradient(135deg, ${theme.colors.champion} 0%, ${theme.colors.darkerChampion} 100%)`;
    default:
      return `linear-gradient(135deg, ${theme.colors.envy} 0%, ${theme.colors.darkerEnvy} 100%)`;
  }
};

const getTierIcon = (tierKey: string) => {
  switch (tierKey) {
    case "ESSENTIALS":
      return <EssentialsIconSVG />;
    case "PRO":
      return <ProIconSVG />;
    default:
      return <EssentialsIconSVG />;
  }
};

const formatHighlight = (text: string) => {
  const parts = text.split(/\*\*(.*?)\*\*/g);
  return parts.map((part, i) =>
    i % 2 === 1 ? <strong key={i}>{part}</strong> : part,
  );
};

const getTierCopy = (userGroup?: string) => {
  switch (userGroup) {
    case "ESSENTIALS":
      return {
        title: "You've Reached Your Clip Limit",
        featureName: "Clip Limit Reached",
        upgradeTierKey: "PRO" as TTierKey,
        buttonLabel: "Upgrade to Standard",
        lookUp: "pro_monthly",
        analyticsId: "clip_eligibility_upgrade_to_pro",
        showUpgradeButton: true,
      };
    case "PRO":
    case "PRO_PLUS":
    case "CREATOR_PLAT":
      return {
        title: "You've Reached Your Clip Limit",
        featureName: "Clip Limit Reached",
        upgradeTierKey: null,
        buttonLabel: null,
        lookUp: null,
        analyticsId: null,
        showUpgradeButton: false,
      };
    case "FREE":
    default:
      return {
        title: "Clips are Limited on Free Plans",
        featureName: "Clip Limit Reached",
        upgradeTierKey: "ESSENTIALS" as TTierKey,
        buttonLabel: "Upgrade to Basic",
        lookUp: "essentials_monthly",
        analyticsId: "clip_eligibility_upgrade_to_essentials",
        showUpgradeButton: true,
      };
  }
};

export const ClipEligibilityModal = ({
  clipQuota,
  userGroup,
  onClose,
  closeQuickEditModal,
}: IClipEligibilityModalProps) => {
  const [paystation, setPaystationOpen] = useRecoilState(StripePaystationState);
  const history = useHistory();
  const tierCopy = getTierCopy(userGroup);
  const nextAvailableAt = clipQuota?.nextAvailableAt;

  const upgradeTierKey = tierCopy.upgradeTierKey;
  const tierData = upgradeTierKey
    ? TIER_DATA.find((t) => t.key === upgradeTierKey)
    : null;
  const tierHighlights = upgradeTierKey
    ? TIER_HIGHLIGHTS[upgradeTierKey]
    : null;
  const accent = upgradeTierKey
    ? TIER_ACCENT[upgradeTierKey] || theme.colors.envy
    : theme.colors.envy;
  const sections = tierHighlights?.sections || [];

  const handleUpgradeClick = () => {
    if (!tierCopy.lookUp || !tierCopy.analyticsId) return;

    analytics.track("Button - Click", {
      locationIdentifier: tierCopy.analyticsId,
      location: "clip_eligibility_modal",
    });

    onClose();
    closeQuickEditModal?.();

    setPaystationOpen({
      ...paystation,
      isModalOpen: true,
      type: "subscribe",
      lookUp: tierCopy.lookUp,
      returnUrl: window.location.pathname,
    });
  };

  const handleSeeAllPlansClick = () => {
    analytics.track("Button - Click", {
      locationIdentifier: "clip_eligibility_see_all_plans",
      location: "clip_eligibility_modal",
    });

    onClose();
    closeQuickEditModal?.();
    history.push("/upgrade");
  };

  return (
    <Container onClick={(e) => e.stopPropagation()}>
      <Header $tierKey={upgradeTierKey || "ESSENTIALS"}>
        <BackgroundIcon $tierKey={upgradeTierKey || "ESSENTIALS"}>
          {getTierIcon(upgradeTierKey || "ESSENTIALS")}
        </BackgroundIcon>

        <HeaderContent>
          <LockRow>
            <LockIcon />
            <FeatureName>{tierCopy.featureName}</FeatureName>
          </LockRow>

          {tierData ? (
            <>
              <UnlockHeading>
                Unlock with{" "}
                <TierAccent $color={accent}>{tierData.name}</TierAccent>
              </UnlockHeading>

              <PriceRow>
                <PriceText>{tierData.pricing.annual}</PriceText>
                <PriceUnit>/month</PriceUnit>
                {tierData.pricing.discount && (
                  <Discount>{tierData.pricing.discount}</Discount>
                )}
              </PriceRow>
            </>
          ) : (
            <UnlockHeading>{tierCopy.title}</UnlockHeading>
          )}
        </HeaderContent>
      </Header>

      <Body>
        {nextAvailableAt && (
          <TimerSection>
            <Flex alignItems="center" style={{ gap: 6 }}>
              <TimerIcon />
              <Text
                variant="text.labelMedium"
                color="chalkAlpha80"
                fontSize="13px"
              >
                Next free clip available in:
              </Text>
            </Flex>
            <Countdown
              date={new Date(nextAvailableAt)}
              renderer={({ days, hours, minutes, seconds }) => {
                if (days > 0) {
                  const timeString = `${hours < 10 ? "0" : ""}${hours}:${
                    minutes < 10 ? "0" : ""
                  }${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;

                  return (
                    <Flex alignItems="center" style={{ gap: 8 }}>
                      <Text
                        color="ice"
                        variant="text.headingSmall"
                        fontWeight={600}
                      >
                        {days} {days === 1 ? "day" : "days"}
                      </Text>
                      <Text
                        color="chalkAlpha60"
                        variant="text.labelMedium"
                        fontWeight={500}
                      >
                        {timeString}
                      </Text>
                    </Flex>
                  );
                }

                return (
                  <CountDownRenderer
                    days={days}
                    hours={hours}
                    minutes={minutes}
                    seconds={seconds}
                    color={theme.colors.ice}
                    variant="text.headingSmall"
                    showEndsIn={false}
                    showIcon={false}
                  />
                );
              }}
            />
          </TimerSection>
        )}

        {sections.map((section: IHighlightSection, sectionIndex: number) => (
          <HighlightSection key={sectionIndex}>
            <SectionTitle>{section.title}</SectionTitle>
            <FeatureList>
              {section.items.map(
                (item: string | IHighlightItem, itemIndex: number) => {
                  const isObject = typeof item === "object";
                  const text = isObject ? item.text : item;
                  return (
                    <FeatureItem key={itemIndex}>
                      <CheckIcon $color={accent} />
                      <FeatureText>{formatHighlight(text)}</FeatureText>
                    </FeatureItem>
                  );
                },
              )}
            </FeatureList>
          </HighlightSection>
        ))}

        {tierCopy.showUpgradeButton && tierData ? (
          <UpgradeButton
            $tierKey={upgradeTierKey || "ESSENTIALS"}
            onClick={handleUpgradeClick}
          >
            {tierCopy.buttonLabel}
          </UpgradeButton>
        ) : null}

        <SeeAllPlans onClick={handleSeeAllPlansClick}>
          See all plans
        </SeeAllPlans>
      </Body>
    </Container>
  );
};

const Container = styled(Box)`
  background: ${({ theme }) => theme.colors.ned};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha10};
  border-radius: 16px;
  box-shadow:
    0 4px 12px ${({ theme }) => theme.colors.blackAlpha24},
    0 2px 4px ${({ theme }) => theme.colors.blackAlpha15};
  max-width: 400px;
  overflow: hidden;
  width: 90vw;
`;

const Header = styled(Box)<{ $tierKey: string }>`
  background: ${({ $tierKey }) => getHeaderGradient($tierKey)};
  overflow: hidden;
  padding: 24px;
  position: relative;
`;

const BackgroundIcon = styled(Box)<{ $tierKey: string }>`
  left: -30px;
  opacity: 0.12;
  position: absolute;
  top: -40px;
  z-index: 0;

  svg {
    height: 120px;
    width: 120px;
    -webkit-mask: linear-gradient(
      to right,
      black 0%,
      black 40%,
      transparent 85%
    );
    mask: linear-gradient(to right, black 0%, black 40%, transparent 85%);
  }
`;

const HeaderContent = styled(Flex)`
  flex-direction: column;
  gap: 12px;
  position: relative;
  z-index: 1;
`;

const LockRow = styled(Flex)`
  align-items: center;
  gap: 8px;
`;

const LockIcon = styled(IoLockClosed)`
  color: ${({ theme }) => theme.colors.chalk};
  height: 16px;
  width: 16px;
`;

const FeatureName = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 14px;
  font-weight: 500;
`;

const UnlockHeading = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 22px;
  font-weight: 700;
`;

const TierAccent = styled.span<{ $color: string }>`
  color: ${({ $color }) => $color};
`;

const PriceRow = styled(Flex)`
  align-items: baseline;
  gap: 4px;
`;

const PriceText = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 28px;
  font-weight: 700;
  line-height: 1;
`;

const PriceUnit = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 14px;
  font-weight: 500;
`;

const Discount = styled(Text)`
  background: ${({ theme }) => theme.colors.envyAlpha10};
  border-radius: 20px;
  color: ${({ theme }) => theme.colors.envy};
  font-size: 11px;
  font-weight: 600;
  margin-left: 8px;
  padding: 3px 10px;
`;

const Body = styled(Box)`
  border-top: 1px solid ${({ theme }) => theme.colors.chalkAlpha10};
  padding: 24px;
`;

const TimerSection = styled(Flex)`
  align-items: center;
  background: ${({ theme }) => theme.colors.chalkAlpha10};
  border-radius: 8px;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
  padding: 16px;
  width: 100%;
`;

const TimerIcon = styled(RiTimeLine)`
  color: ${({ theme }) => theme.colors.chalkAlpha80};
  height: 16px;
  width: 16px;
`;

const HighlightSection = styled(Box)`
  margin-bottom: 16px;

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
  margin-bottom: 10px;
  text-transform: uppercase;
`;

const FeatureList = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const FeatureItem = styled(Flex)`
  align-items: center;
  gap: 8px;
`;

const CheckIcon = styled(IoCheckmark)<{ $color: string }>`
  color: ${({ $color }) => $color};
  min-height: 18px;
  min-width: 18px;
`;

const FeatureText = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 14px;
  font-weight: 500;

  strong {
    color: ${({ theme }) => theme.colors.ice};
    font-weight: 700;
  }
`;

const UpgradeButton = styled.button<{ $tierKey: string }>`
  background: ${({ $tierKey }) => getButtonGradient($tierKey)};
  border: none;
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  margin-top: 24px;
  padding: 12px 20px;
  transition: filter 0.2s ease;
  width: 100%;

  &:hover {
    filter: brightness(1.1);
  }
`;

const SeeAllPlans = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  font-size: 14px;
  margin-top: 12px;
  text-align: center;
  text-decoration: underline;
  text-decoration-color: ${({ theme }) => theme.colors.chalkAlpha40};
  transition: color 0.2s ease;
  width: 100%;

  &:hover {
    color: ${({ theme }) => theme.colors.ice};
  }
`;
